import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { FaLock, FaEye, FaEyeSlash, FaUser, FaArrowLeft, FaCheck, FaTimes, FaEnvelope } from 'react-icons/fa';
import '../styles/colors'
const SignUp = () => {
  const { signUp } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countryCode, setCountryCode] = useState('+1');
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [success, setSuccess] = useState('');


  useEffect(() => {
    const checkPasswordRequirements = () => {
      setPasswordRequirements({
        minLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      });
    };

    checkPasswordRequirements();
  }, [password]);

  // Scroll to top whenever a success or error message appears, so users on small screens
  // (where the signup form is long and the message sits above the fold) don't miss it.
  useEffect(() => {
    if (error || success) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, success]);

  const formatPhoneInput = (input, code) => {
    if (!input) return '';
    const digits = input.replace(/\D/g, '');
    if (code === '+1') {
      // US format: (XXX) XXX-XXXX
      const tenDigits = (digits.length === 11 && digits.startsWith('1'))
        ? digits.slice(1)
        : digits.slice(0, 10);
      if (tenDigits.length === 0) return '';
      if (tenDigits.length <= 3) return `(${tenDigits}`;
      if (tenDigits.length <= 6) return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3)}`;
      return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
    }
    // India format: XXXXX XXXXX
    const tenDigits = digits.slice(0, 10);
    if (tenDigits.length <= 5) return tenDigits;
    return `${tenDigits.slice(0, 5)} ${tenDigits.slice(5)}`;
  };

  const normalizePhoneToE164 = (input, countryCode) => {
    let digits = (input || '').replace(/\D/g, '');
    if (!digits) return null;

    // Israel: strip a single leading 0 if user typed local format (e.g. 0501234567 → 501234567)
    if (countryCode === '+972' && digits.length === 10 && digits.startsWith('0')) {
      digits = digits.slice(1);
    }

    return `${countryCode}${digits}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Check if all password requirements are met
    const allRequirementsMet = Object.values(passwordRequirements).every(req => req);
    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }

    // const normalizedPhone = normalizePhoneToE164(phoneInput);
    // if (!normalizedPhone) {
    //   setError('Please enter a valid US phone number (10 digits).');
    //   return;
    // }

    const normalizedPhone = normalizePhoneToE164(phoneInput, countryCode);

    setLoading(true);

    try {
      const { data, error } = await signUp(email, password, {
        data: {
          first_name: firstName,
          last_name: lastName,
          phone_number: normalizedPhone
        }
      });

      if (error) {
        // Translate Supabase's generic auth errors to user-friendly messages.
        // "Database error saving new user" surfaces when our profiles trigger hits a unique
        // constraint (typically the phone number) — same shape returned for any DB-level conflict.
        const raw = (error.message || '').toLowerCase();
        let userMsg = error.message;
        if (raw.includes('database error saving new user') || raw.includes('duplicate') || raw.includes('unique constraint')) {
          userMsg = 'This email or phone number is already registered. Please log in instead, or use "Forgot Password" if you need to reset.';
        } else if (raw.includes('user already registered') || raw.includes('already exists') || raw.includes('already been registered')) {
          userMsg = 'An account with this email already exists. Please log in instead.';
        } else if (raw.includes('invalid email') || (raw.includes('email') && raw.includes('invalid'))) {
          userMsg = 'Please enter a valid email address.';
        } else if (raw.includes('password') && (raw.includes('weak') || raw.includes('short'))) {
          userMsg = 'Password is too weak. Please meet all the requirements shown above.';
        } else if (raw.includes('rate limit') || raw.includes('too many')) {
          userMsg = 'Too many signup attempts. Please wait a few minutes and try again.';
        }
        setError(userMsg);
        return;
      }

      // Detect Supabase's user-enumeration protection response: when an existing confirmed email
      // is used for signup, Supabase returns error=null with a user object whose `identities`
      // array is empty (no new identity was created). No verification email gets sent in this case.
      // Without this check, the UI would incorrectly show "Account created — check your email."
      if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        setError('An account with this email already exists. Please log in instead, or use "Forgot Password" to reset.');
        return;
      }

      // Only proceed if we have a user and no error
      if (data?.user) {
        // Show verification message — include the email so the user knows which inbox to check
        setSuccess(`Account created successfully! Please check ${email} to verify your account.`);
        // Clear form so a returning visitor doesn't see stale credentials on screen
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneInput('');
        setPassword('');
        setConfirmPassword('');
      } else {
        setError('Failed to create an account. Please try again.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('Failed to create an account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Link to="/" className="back-to-home">
          <FaArrowLeft /> Back to Home
        </Link>

        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">Sign up for your Pre Go Prep account</p>

        {error && (
          <div className="auth-error">
            {error}
            {error.includes('already registered') && (
              <div className="auth-error-action">
                <Link to="/login" className="auth-link">Sign in here</Link>
              </div>
            )}
            {error.includes('wait a minute') && (
              <div className="auth-error-action">
                Please try again in one minute. This limit is only during testing.
              </div>
            )}
          </div>
        )}

        {success && (
          <div className="auth-success">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaUser className="input-icon" />
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First Name"
                required
                className="auth-input"
              />
            </div>
          </div>

          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaUser className="input-icon" />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last Name"
                required
                className="auth-input"
              />
            </div>
          </div>

          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                className="auth-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '4px', display: 'block' }}>
              Phone Number <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>(optional)</span>
            </label>
            <div style={{
              display: 'flex',
              width: '100%',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--border-radius)',
              overflow: 'hidden',
              backgroundColor: '#fff',
            }}>
              <select
                value={countryCode}
                onChange={(e) => { setCountryCode(e.target.value); setPhoneInput(''); }}
                style={{
                  border: 'none',
                  borderRight: '1px solid #e5e7eb',
                  padding: '12px 8px 12px 12px',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  color: 'inherit',
                  backgroundColor: '#f8f9fa',
                  outline: 'none',
                  cursor: 'pointer',
                  width: '85px',
                  flexShrink: 0,
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'8\' height=\'5\' viewBox=\'0 0 8 5\'%3E%3Cpath fill=\'%23666\' d=\'M0 0l4 5 4-5z\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                }}
              >
                <option value="+1">+1</option>
                <option value="+91">+91</option>
                <option value="+972">+972</option>
              </select>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => setPhoneInput(formatPhoneInput(e.target.value, countryCode))}
                placeholder={
                  countryCode === '+1' ? '(555) 555-5555'
                  : countryCode === '+972' ? '054 123 4567'
                  : '98765 43210'
                }
                style={{
                  border: 'none',
                  padding: '12px 14px',
                  fontSize: '1rem',
                  lineHeight: 1.5,
                  color: 'inherit',
                  outline: 'none',
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: 'transparent',
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="auth-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div className="password-requirements">
              <h4>Password Requirements:</h4>
              <ul>
                <li className={passwordRequirements.minLength ? 'requirement-met' : ''}>
                  {passwordRequirements.minLength ? <FaCheck /> : <FaTimes />}
                  Minimum 8 characters
                </li>
                <li className={passwordRequirements.hasUppercase ? 'requirement-met' : ''}>
                  {passwordRequirements.hasUppercase ? <FaCheck /> : <FaTimes />}
                  At least one uppercase letter
                </li>
                <li className={passwordRequirements.hasLowercase ? 'requirement-met' : ''}>
                  {passwordRequirements.hasLowercase ? <FaCheck /> : <FaTimes />}
                  At least one lowercase letter
                </li>
                <li className={passwordRequirements.hasNumber ? 'requirement-met' : ''}>
                  {passwordRequirements.hasNumber ? <FaCheck /> : <FaTimes />}
                  At least one number
                </li>
                <li className={passwordRequirements.hasSpecial ? 'requirement-met' : ''}>
                  {passwordRequirements.hasSpecial ? <FaCheck /> : <FaTimes />}
                  At least one special character
                </li>
              </ul>
            </div>
          </div>

          <div className="form-group">
            <div className="input-icon-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm Password"
                required
                className={`auth-input ${password && confirmPassword && password !== confirmPassword ? 'auth-input-error' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {password && confirmPassword && password !== confirmPassword && (
              <div className="input-error-message">Passwords do not match</div>
            )}
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <p style={{
            fontSize: '0.9rem',
            color: '#6c757d',
            textAlign: 'center',
            marginTop: '1rem'
          }}>
            By clicking Sign up you agree to the Pre Go Prep{' '}
            <Link to="/terms-of-use" style={{ color: '#0d6efd', textDecoration: 'none' }}>
              terms of use
            </Link>{' '}
            and{' '}
            <Link to="/privacy-policy" style={{ color: '#0d6efd', textDecoration: 'none' }}>
              privacy policy
            </Link>
          </p>
        </form>

        <div className="auth-links">
          <div className="auth-separator">
            <span>Already have an account?</span>
          </div>
          <Link to="/login" className="auth-link-secondary">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignUp;